import React, { useState, useEffect, useCallback, memo } from "react";
import Pagination from "../pagination";
import ReferralTable from "./ReferralTable";
import AddReferralModal, { ReferralFormConfig } from "./AddReferralModal";
import ConfirmDeleteModal from "../DeleteModel";
import { useToastStore } from "store/toastStore";
import {
  getAllReferralCreditConfig,
  createReferralCreditConfig,
  updateReferralCreditConfig,
  deleteReferralCreditConfig,
  ReferralCreditConfigAPI,
  ReferralCreditConfigRequest,
} from "apis/adminServices/referralServices";

const EMPTY_CONFIG: ReferralFormConfig = {
  configName: "",
  referrerCreditAmount: 0,
  refereeCreditAmount: 0,
  nonCommunityReferrerCredit: 0,
  nonCommunityRefereeCredit: 0,
  communityCount: 0,
  isActive: true,
  validTill: null,
};

const ReferralManagement: React.FC = () => {
  const { showToast } = useToastStore();

  const [configurations, setConfigurations] = useState<ReferralCreditConfigAPI[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newConfig, setNewConfig] = useState<ReferralFormConfig>(EMPTY_CONFIG);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllReferralCreditConfig();
      setConfigurations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch Error:", err);
      setConfigurations([]);
      showToast("Failed to fetch referral configurations", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const paginated = configurations.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const openAddModal = useCallback(() => {
    setNewConfig(EMPTY_CONFIG);
    setIsAddOpen(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setIsAddOpen(false);
    setNewConfig(EMPTY_CONFIG);
  }, []);

  const handleAddConfig = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        setAdding(true);
        const response = await createReferralCreditConfig(newConfig);
        showToast(
          response.message || "Referral configuration created successfully",
          "success"
        );
        setIsAddOpen(false);
        setNewConfig(EMPTY_CONFIG);
        await fetchConfigurations();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create referral configuration";
        showToast(errorMessage, "error");
      } finally {
        setAdding(false);
      }
    },
    [newConfig, fetchConfigurations, showToast]
  );

  const handleUpdateConfig = useCallback(
    async (id: number, payload: ReferralCreditConfigRequest) => {
      try {
        const response = await updateReferralCreditConfig(id, payload);
        showToast(response.message || "Referral configuration updated successfully", "success");
        await fetchConfigurations();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update referral configuration";
        showToast(errorMessage, "error");
        throw error;
      }
    },
    [fetchConfigurations, showToast]
  );

  const handleDelete = useCallback((id: number, name: string) => {
    setItemToDelete({ id, name });
  }, []);

  const cancelDelete = useCallback(() => {
    if (deleting) return;
    setItemToDelete(null);
  }, [deleting]);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);
      await deleteReferralCreditConfig(itemToDelete.id);
      showToast("Referral configuration deleted successfully", "success");
      // If we just removed the last row on the current page, step back one page.
      const remaining = configurations.length - 1;
      const maxPage = Math.max(1, Math.ceil(remaining / pageSize));
      if (currentPage > maxPage) setCurrentPage(maxPage);
      await fetchConfigurations();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete referral configuration";
      showToast(errorMessage, "error");
    } finally {
      setDeleting(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, configurations.length, pageSize, currentPage, fetchConfigurations, showToast]);

  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
        <p className="text-grease-100 text-sm font-semibold">Referral Management</p>
        <button
          type="button"
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg hover:bg-[#E6C200] transition-colors"
          disabled={loading}
        >
          <span className="st-add text-white" />
          <p className="font-medium text-white text-sm">Add New</p>
        </button>
      </div>

      <ReferralTable
        configurations={paginated}
        loading={loading}
        onUpdate={handleUpdateConfig}
        onDelete={handleDelete}
      />

      {isAddOpen && (
        <AddReferralModal
          newConfig={newConfig}
          setNewConfig={setNewConfig}
          onAdd={handleAddConfig}
          onClose={closeAddModal}
          loading={adding}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalItems={configurations.length}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      <ConfirmDeleteModal
        isOpen={itemToDelete !== null}
        configurationName={itemToDelete?.name ?? ""}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </section>
  );
};

export default memo(ReferralManagement);
